import { useHandler } from "@/hooks";
import { createStaticJsxRuntime } from "@/lib/syntax-highlighting-utils";
import { parameterAwareDebounce } from "@/lib/utils/parameterAwareDebounce";
import {
  type Options as JsxRuntimeOptions,
  toJsxRuntime,
} from "hast-util-to-jsx-runtime";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

// Type from hast-util-to-jsx-runtime
type Nodes = Parameters<typeof toJsxRuntime>[0];

export interface UseSyntaxHighlighterOptions {
  language: string;
  content: string;

  // Optional streaming enhancements
  streaming?: {
    enabled: boolean;
    animationDuration?: string;
  };

  // Customization options
  jsxRuntimeFactory?: () => JsxRuntimeOptions;
  workerConfig?: {
    className?: string;
    lineNumberOffset?: number;
    debounceMs?: number;
  };

  // Enable/disable highlighting
  enabled?: boolean;
}

export interface UseSyntaxHighlighterResult {
  processedContent: ReactNode | null;
  isReady: boolean;
  isProcessing: boolean;
  error: string | null;
}

export function useSyntaxHighlighter({
  language,
  content,
  streaming: _streaming = { enabled: false },
  jsxRuntimeFactory,
  workerConfig = {},
  enabled = true,
}: UseSyntaxHighlighterOptions): UseSyntaxHighlighterResult {
  const [processedContent, setProcessedContent] = useState<ReactNode | null>(
    null,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Worker state management
  const [workerState, setWorkerState] = useState<{
    isWorkerReady: boolean;
    updateWorker: (content: string) => void;
    cancel: () => void;
  } | null>(null);

  // Default worker config
  const {
    className = "",
    lineNumberOffset = 0,
    debounceMs = 1000 / 30, // 30fps
  } = workerConfig;

  const lastContentRef = useRef<string | null>(null);

  // Create JSX runtime - use factory or default static runtime
  const jsxRuntime = useMemo(() => {
    if (jsxRuntimeFactory) {
      return jsxRuntimeFactory();
    }

    // Use shared static JSX runtime
    return createStaticJsxRuntime();
  }, [jsxRuntimeFactory]);

  // Process content handler - stable reference
  const processContent = useHandler((hast: Nodes) => {
    setProcessedContent(toJsxRuntime(hast, jsxRuntime));
    setIsProcessing(false);
    setError(null);
  });

  // Error handler - stable reference
  const handleError = useHandler((errorMessage: string) => {
    console.error("Syntax highlighting error:", errorMessage);
    setError(errorMessage);
    setIsProcessing(false);

    // Fallback to plain text
    setProcessedContent(
      <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
        {content}
      </pre>,
    );
  });

  useEffect(() => {
    if (!enabled) {
      setProcessedContent(
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
          {content}
        </pre>,
      );
      return;
    }

    // Clear content while waiting for worker to be ready
    setProcessedContent(null);
  }, [enabled, content]);

  // Worker setup effect
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const worker = new Worker(
      new URL(
        "../components/CodeGen/SourceCode/syntaxWorker.ts",
        import.meta.url,
      ),
      { type: "module" },
    );

    const updateWorker = parameterAwareDebounce<
      (content: string) => void,
      [string]
    >((content) => {
      if (!ready) {
        return;
      }

      lastContentRef.current = content;
      setIsProcessing(true);
      worker.postMessage({
        content,
        language,
        className,
        lineNumberOffset,
      });
    }, debounceMs);

    let ready = false;

    worker.onmessage = (e) => {
      const { type, hast, error } = e.data;

      if (type === "ready") {
        ready = true;
        setWorkerState({
          isWorkerReady: true,
          updateWorker,
          cancel: () => updateWorker.cancel(),
        });
        return;
      }

      if (type === "success" && hast) {
        processContent(hast);
        return;
      }

      if (type === "error" && error) {
        handleError(error);
        return;
      }
    };

    // Initialize worker state as not ready
    setWorkerState({
      isWorkerReady: false,
      updateWorker,
      cancel: () => updateWorker.cancel(),
    });

    // Clean up
    return () => {
      updateWorker.cancel();
      worker.terminate();
      setWorkerState(null);
    };
  }, [
    enabled,
    language,
    className,
    lineNumberOffset,
    debounceMs,
    processContent,
    handleError,
    // jsxRuntime,
  ]);

  // Update worker when content changes
  useEffect(() => {
    if (workerState?.isWorkerReady && enabled) {
      workerState.updateWorker(content);
    }
  }, [workerState?.isWorkerReady, workerState?.updateWorker, content, enabled]);

  return {
    processedContent,
    isReady: workerState?.isWorkerReady ?? false,
    isProcessing,
    error,
  };
}
